import type { SignupRegion } from '@server/signup-answers';
import { useEffect, useId, useRef } from 'react';

import { Button } from '@/_shared/components/ui/button';

import type { HostedUiConfig } from '../../lib/hosted-ui';
import { REGION_FIELD_LABEL, REGION_HINT } from '../../lib/signup-regions';
import { FederatedStartButton } from './FederatedStartButton';
import { GoogleGlyph } from './GoogleGlyph';
import { RegionRadioGroup } from './RegionRadioGroup';
import { RegionScopeTooltip } from './RegionScopeTooltip';

/**
 * "Sign up with Google" on `/signup`. As in the web app, a new account gets its region before Google opens, so the
 * button opens the region step. An invitee gets no RudderStack organization, so it picks no region.
 */
export function GoogleSignupButton({
  hostedUi,
  returnTo,
  invite,
  onPickRegion,
  onStart,
  ref,
}: {
  /** The Hosted UI for the page flow with the shared session (ACT2-879), or null for Lookout's server flow. */
  hostedUi: HostedUiConfig | null;
  returnTo: string | null;
  invite: boolean;
  onPickRegion: () => void;
  /** Called on the click, so the page can hide the error of an earlier attempt. */
  onStart?: () => void;
  ref?: React.Ref<HTMLButtonElement>;
}): React.ReactElement {
  if (invite) {
    return (
      <FederatedStartButton hostedUi={hostedUi} options={{ returnTo, invite: true, page: 'signup' }} onStart={onStart}>
        <GoogleGlyph />
        Sign up with Google
      </FederatedStartButton>
    );
  }

  return (
    <Button
      ref={ref}
      type="button"
      variant="secondary"
      size="lg"
      className="w-full gap-2"
      Icon={<GoogleGlyph />}
      onClick={() => {
        onStart?.();
        onPickRegion();
      }}
    >
      Sign up with Google
    </Button>
  );
}

/** The region step of the Google sign-up. Google opens only on Continue, with the chosen region. */
export function GoogleRegionStep({
  hostedUi,
  returnTo,
  region,
  onRegionChange,
  onBack,
}: {
  hostedUi: HostedUiConfig | null;
  returnTo: string | null;
  region: SignupRegion;
  onRegionChange: (region: SignupRegion) => void;
  onBack: () => void;
}): React.ReactElement {
  const questionId = useId();
  const selected = useRef<HTMLButtonElement>(null);
  // The button that opened the step is gone, so its focus moves to the chosen region.
  useEffect(() => {
    selected.current?.focus();
  }, []);

  return (
    <div className="flex flex-col gap-4 text-left">
      <div className="flex flex-col gap-2">
        <div className="flex items-center gap-1">
          <p id={questionId} className="text-sm font-medium text-foreground">
            {REGION_FIELD_LABEL}
          </p>
          <RegionScopeTooltip />
        </div>
        <p className="text-xs text-muted-foreground">{REGION_HINT}</p>
        <RegionRadioGroup
          aria-labelledby={questionId}
          value={region}
          onValueChange={onRegionChange}
          selectedRef={selected}
        />
      </div>
      <div className="grid grid-cols-2 gap-2">
        <Button type="button" variant="secondary" size="lg" className="w-full" onClick={onBack}>
          Back
        </Button>
        <FederatedStartButton
          hostedUi={hostedUi}
          variant="primary"
          options={{ returnTo, invite: false, region, page: 'signup' }}
        >
          Continue
        </FederatedStartButton>
      </div>
    </div>
  );
}
